/**
 * Challenge family — the five `challenge_*` types plus the opponent search.
 *
 * Split of responsibility (M2 spec §4bis, checked against the old code):
 *  - the INCOMING-challenge modal is NOT ours. `src/Components/ChallengeComponent`
 *    is mounted app-wide, registers its own handlers (`challenge_request_check`,
 *    `challenge_cancel`, `challenge_request_expire`), shows the card, rejects, and
 *    on Accept writes the `challengePacket` handshake, navigates here and sends
 *    `challenge_accept_response` 800 ms later. It keeps working untouched, so this
 *    reducer only RECORDS an inbound `challenge_game_request` (its terms are what
 *    a later cancel/rematch packet is rebuilt from) and draws nothing;
 *  - what PlayChess itself owned is what is ported here: the fate of a challenge
 *    *I* sent (reject / cancel / expire) and the opponent-search result.
 *
 * PlayChess refs: `challengeCancelCallback` L2432-2443, `registerChallengeRequest`
 * L2452-2457, `challengeRejectedCallback` L2788-2797, `challengeExpiredCallback`
 * L2799-2823, `onlinePlayersSearchResult` L2834-2840, `registerOnlinePlayersSearchSchema`
 * L2830-2832 (the echo, a no-op).
 *
 * Pure, like every reducer here: one new state object, `now` injected — plus
 * the three named `bridge*` calls, which is how every reducer in this tree
 * mirrors a cross-screen flag (`gamePlayStoreWrites.ts`). Those three matter
 * here more than anywhere else: they are what tells the app-wide
 * `Components/ChallengeComponent` the fate of a challenge I sent while I am on
 * SOME OTHER screen (P1-10) — the Play screen is unmounted then, and on this
 * lane nothing else was setting them.
 */
import {
  bridgeSetShowChallengeCancel,
  bridgeSetShowChallengeExpire,
  bridgeSetShowChallengeReject,
} from '../gamePlayStoreWrites';
import {
  ChallengeSlice,
  IncomingChallenge,
  LiveGameState,
  OnlinePlayer,
  START_FEN,
  id,
} from '../state';

export const CHALLENGE_TYPES = [
  'challenge_cancel',
  'challenge_reject',
  'challenge_request_expire',
  'challenge_game_request',
  'online_players_search',
  'online_players_result',
];

const withChallenge = (
  state: LiveGameState,
  challenge: Partial<ChallengeSlice>,
): LiveGameState => ({
  ...state,
  challenge: {...state.challenge, ...challenge},
});

export function challengeReducer(
  state: LiveGameState,
  type: string,
  data: any,
  now: number,
): LiveGameState {
  switch (type) {
    case 'challenge_reject':
      return challengeReject(state, now);
    case 'challenge_cancel':
      return challengeCancel(state, data, now);
    case 'challenge_request_expire':
      return challengeExpire(state, data);
    case 'challenge_game_request':
      return challengeRequest(state, data);
    case 'online_players_result':
      return onlinePlayersResult(state, data);
    case 'online_players_search':
      // The server echoes our own search back and PlayChess registers a no-op
      // handler for it (L2830-2832). Routed on purpose: an unrouted type would
      // be an invisible hole in the parity harness.
      return state;
    default:
      return state;
  }
}

/**
 * The opponent said no — L2788-2797 (`setShowChallengeReject(true)` +
 * `setChallengeInProgress(false)`). The waiting card goes away and the lobby
 * says so.
 */
function challengeReject(state: LiveGameState, now: number): LiveGameState {
  bridgeSetShowChallengeReject(true);
  return withChallenge(state, {
    outgoing: null,
    notice: {kind: 'rejected', at: now},
  });
}

/**
 * L2432-2443. The server broadcasts the cancel to the CHALLENGED player's user
 * room with `userId` = that player (joinLeaveProcessor L374-390), so the guard
 * is "is this addressed to me" — PlayChess leans on the room alone and shows the
 * modal for any cancel that reaches it.
 *
 * That `userId` is `data.opponent` copied VERBATIM from the canceller's packet,
 * and both PlayChess (L3712-3735) and `actions.cancelChallenge` pack that field
 * `<id>:<username>` — so the addressee arrives PACKED here (the room name is
 * split, the payload field is not). Hence the split: bare ids still match, which
 * is what a cancel built from a bare `opponent` (ChallengeComponent L296, L370)
 * would carry.
 */
function challengeCancel(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  const addressee = id(String(data?.userId ?? '').split(':')[0]);
  if (addressee !== state.session.me) {
    return state;
  }
  // L3684: the global component draws "Challenge Cancelled!" from this flag, so
  // it is the one notice that survives an unmounted Play screen.
  bridgeSetShowChallengeCancel(true);
  return withChallenge(state, {
    outgoing: null,
    incoming: null,
    notice: {kind: 'cancelled', at: now},
  });
}

/**
 * L2799-2823 — DISMISS-ONLY, never a notice: the server reuses this type as
 * "accepted elsewhere" (joinLeaveProcessor L509-529 dismisses the modal on the
 * accepter's other tabs) and arena auto-pairing fires it between games, so an
 * "expired" card would pop out of nowhere.
 *
 * The requestId gate is ours: the expiry timer echoes the challenge's own
 * requestId (timerProcessor L376-386), and an expiry that belongs to SOMEBODY
 * ELSE's challenge (one sent to me while mine is still out) must not clear mine.
 */
function challengeExpire(state: LiveGameState, data: any): LiveGameState {
  const outgoing = state.challenge.outgoing;
  const requestId = data?.requestId ? String(data.requestId) : '';
  if (outgoing && requestId && requestId !== outgoing.requestId) {
    return state;
  }
  // FALSE, not true — L4279/L4289 only ever clear it. Raising it here would pop
  // "The challenge has expired!" out of nowhere between two arena games.
  bridgeSetShowChallengeExpire(false);
  return withChallenge(state, {outgoing: null, incoming: null});
}

/**
 * Somebody challenged ME (L2452-2457). No UI here (the app-wide component draws
 * it); the terms are recorded because the accept/cancel packets are rebuilt from
 * them. Ignored while a game is on: PlayChess gates the same message on
 * `!playingRef.current` in the global component (ChallengeComponent L269-273).
 */
function challengeRequest(state: LiveGameState, data: any): LiveGameState {
  if (state.game.phase === 'starting' || state.game.phase === 'playing') {
    return state;
  }
  const packed = String(data?.opponent || '');
  const incoming: IncomingChallenge = {
    requestId: data?.requestId ? String(data.requestId) : '',
    opponentId: id(packed.split(':')[0]),
    opponentName: packed.split(':')[1] || '',
    tcMs: Number(data?.playerMoveTimer) || 0,
    incMs: Number(data?.moveTimeIncrement) || 0,
    fen: data?.gameState || START_FEN,
    white: data?.white ? String(data.white) : '',
    gamePlayMode: data?.gamePlayMode || 'Play',
    variantId: Number(data?.variantId) || 0,
    tableId: data?.tableId === undefined ? null : Number(data.tableId),
  };
  return withChallenge(state, {incoming});
}

/**
 * L2834-2840 + ChallengeUserComponent L142-162: `players` is a JSON STRING of
 * `{[username]: userId}`. It is reversed HERE, once, so no consumer has to know
 * the wire shape (and a malformed payload cannot throw inside a render).
 */
function onlinePlayersResult(state: LiveGameState, data: any): LiveGameState {
  return {
    ...state,
    lobby: {...state.lobby, searchResults: parsePlayers(data?.players)},
  };
}

export function parsePlayers(players: any): OnlinePlayer[] {
  if (!players) {
    return [];
  }
  let parsed: any = players;
  if (typeof players === 'string') {
    try {
      parsed = JSON.parse(players);
    } catch (e) {
      return [];
    }
  }
  if (!parsed || typeof parsed !== 'object') {
    return [];
  }
  return Object.keys(parsed).map(username => ({
    userId: id(parsed[username]),
    username,
  }));
}
